import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { tournaments as tournamentsApi, rounds } from "../lib/api";
import api from "../lib/api";
import TeammateSearch from "../components/TeammateSearch";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { getGameColor, getGameName } from "../constants/theme";
import { AddToCalendar } from "../components/AddToCalendar";
import { toast } from "sonner";
import {
  Trophy, Gamepad2, Calendar, MapPin, Users, Euro, Share2,
  Download, Smartphone, Check, ArrowLeft, Swords, Clock, Crown, Copy, Bookmark,
  Pencil, UserCheck, UserX, X, Save, CalendarPlus, CalendarMinus, ThumbsDown,
  MessageSquare, Globe, Heart, Store, Bell, ListOrdered,
} from "lucide-react";
import TeamsList from "../components/TeamsList";
import TeamRegistrationModal from "../components/TeamRegistrationModal";
import StripePaymentModal from "../components/StripePaymentModal";
import DOMPurify from "dompurify";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const absUrl = (u) => {
  if (!u) return null;
  return (u.startsWith("http") || u.startsWith("data:")) ? u : `${API_URL}${u}`;
};

const sanitizeHtml = (html) => DOMPurify.sanitize(html || "", {
  ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li", "a", "span", "div", "h1", "h2", "h3", "h4", "blockquote"],
  ALLOWED_ATTR: ["href", "target", "rel"],
});


export default function PlayerEventPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [standings, setStandings] = useState([]);
  const [currentRound, setCurrentRound] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [calendarLinks, setCalendarLinks] = useState({});
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [offerRemaining, setOfferRemaining] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());

  // Live clock (updates every 30s) for the event countdown
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Party management states
  const [interestedUsers, setInterestedUsers] = useState([]);
  const [loadingInterested, setLoadingInterested] = useState(false);
  const [confirmingUser, setConfirmingUser] = useState(null);
  const [rejectingUser, setRejectingUser] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const isTournament = type === "tournament";
  const [orgProfile, setOrgProfile] = useState(null);
  const [isFavShop, setIsFavShop] = useState(false);
  const [favShopLoading, setFavShopLoading] = useState(false);

  const isShopTournament = event?.source === "scraper";

  const loadEvent = useCallback(async () => {
    try {
      const endpoint = isTournament ? `/public/tournaments/${id}` : `/public/availabilities/${id}`;
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/api${endpoint}`, { headers });
      setEvent(res.data);

      // Load organizer profile for stats
      const orgId = res.data.organizer_id || res.data.user_id;
      if (orgId) {
        try {
          const orgRes = await axios.get(`${API_URL}/api/users/${orgId}`);
          setOrgProfile(orgRes.data);
        } catch {}
      }

      // Load standings if tournament is in progress
      if (isTournament && res.data.status === "in_progress") {
        try {
          const [standRes, roundsRes] = await Promise.all([
            axios.get(`${API_URL}/api/tournaments/${id}/standings`),
            axios.get(`${API_URL}/api/tournaments/${id}/rounds`),
          ]);
          setStandings(standRes.data);
          if (roundsRes.data.length > 0) setCurrentRound(roundsRes.data[roundsRes.data.length - 1]);
        } catch {}
      }
    } catch {
      toast.error("Événement non trouvé");
    } finally {
      setLoading(false);
    }
  }, [id, isTournament]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  // Check if shop is favorited
  useEffect(() => {
    if (!isAuthenticated || !event) return;
    const shopId = event.shop_id;
    if (!shopId) return;
    const token = localStorage.getItem("access_token");
    axios.get(`${API_URL}/api/users/me/favorite-shops`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const ids = (res.data || []).map(s => s.id);
        setIsFavShop(ids.includes(shopId));
      })
      .catch(() => {});
  }, [event, isAuthenticated]);

  // ── Automatic waitlist status ──
  const loadWaitlistStatus = useCallback(async () => {
    if (!isAuthenticated || !isTournament) return;
    try {
      const res = await tournamentsApi.waitlistStatus(id);
      setWaitlistStatus(res.data);
    } catch { setWaitlistStatus(null); }
  }, [id, isTournament, isAuthenticated]);

  useEffect(() => {
    if (event?.waitlist_mode === 'automatic') loadWaitlistStatus();
  }, [event, loadWaitlistStatus]);

  // Countdown for an active offer + poll status while waiting
  useEffect(() => {
    const offer = waitlistStatus?.offer;
    if (!offer?.expires_at) { setOfferRemaining(null); return; }
    const tick = () => {
      const diff = new Date(offer.expires_at).getTime() - Date.now();
      setOfferRemaining(diff > 0 ? diff : 0);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [waitlistStatus]);

  useEffect(() => {
    if (event?.waitlist_mode !== 'automatic' || !waitlistStatus?.in_waitlist) return;
    const poll = setInterval(loadWaitlistStatus, 20000);
    return () => clearInterval(poll);
  }, [event, waitlistStatus, loadWaitlistStatus]);

  const handleJoinWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      const res = await tournamentsApi.joinWaitlist(id);
      toast.success(res.data.message || "Ajouté à la file d'attente");
      await loadWaitlistStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setWaitlistLoading(false); }
  };
  const handleLeaveWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      await tournamentsApi.leaveWaitlist(id);
      toast.info("Vous avez quitté la file d'attente");
      await loadWaitlistStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setWaitlistLoading(false); }
  };
  const handleConfirmWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      const res = await tournamentsApi.confirmWaitlist(id);
      toast.success(res.data.message || "Place confirmée !");
      await loadWaitlistStatus();
      await loadEvent();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); await loadWaitlistStatus(); }
    finally { setWaitlistLoading(false); }
  };
  const handleDeclineWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      await tournamentsApi.declineWaitlist(id);
      toast.info("Offre refusée");
      await loadWaitlistStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setWaitlistLoading(false); }
  };
  const loadInterestedUsers = useCallback(async () => {
    if (!isAuthenticated || isTournament) return;
    setLoadingInterested(true);
    try {
      const res = await api.get(`/availabilities/${id}/interested`);
      setInterestedUsers(res.data);
    } catch { setInterestedUsers([]); }
    finally { setLoadingInterested(false); }
  }, [id, isTournament, isAuthenticated]);

  useEffect(() => {
    if (event && !isTournament && (event.user_id === user?.id || user?.role === "ADMIN")) {
      loadInterestedUsers();
    }
  }, [event, user, isTournament, loadInterestedUsers]);

  const handleConfirmUser = async (userId) => {
    setConfirmingUser(userId);
    try {
      await api.post(`/availabilities/${id}/confirm/${userId}`);
      toast.success("Joueur accepté !");
      await loadEvent();
      await loadInterestedUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setConfirmingUser(null); }
  };

  const handleRejectUser = async (userId) => {
    setRejectingUser(userId);
    try {
      await api.delete(`/availabilities/${id}/reject/${userId}`);
      toast.success("Joueur refusé");
      await loadInterestedUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setRejectingUser(null); }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/availabilities/${id}`, editData);
      toast.success("Partie modifiée !");
      setEditing(false);
      await loadEvent();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const startEditing = () => {
    setEditData({
      description: event.description || "",
      max_players: event.max_players || 4,
    });
    setEditing(true);
  };

  // App download popup removed on web per user request.

  // Helper to generate calendar links for an event
  const generateCalendarLinks = (evt) => {
    if (!evt) return {};
    const title = encodeURIComponent(evt.name || `Partie ${getGameName(evt.game)}`);
    const startDate = new Date(evt.date);
    const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000); // +3h default
    const toGCal = (d) => d.toISOString().replace(/-|:|\.\d+/g, '');
    const location = encodeURIComponent(evt.venue?.name || evt.location?.name || evt.venue?.city || evt.location?.city || '');
    const details = encodeURIComponent(evt.description || '');
    const google = `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${toGCal(startDate)}/${toGCal(endDate)}&details=${details}&location=${location}`;
    // ICS for Apple/iOS
    const pad = (n) => String(n).padStart(2, '0');
    const toICS = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
    const icsContent = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
      `DTSTART:${toICS(startDate)}`, `DTEND:${toICS(endDate)}`,
      `SUMMARY:${evt.name || `Partie ${getGameName(evt.game)}`}`,
      `LOCATION:${evt.venue?.name || evt.location?.name || evt.venue?.city || evt.location?.city || ''}`,
      `DESCRIPTION:${(evt.description || '').replace(/\n/g, '\\n')}`,
      'END:VEVENT', 'END:VCALENDAR'
    ].join('\r\n');
    const icsBlob = new Blob([icsContent], { type: 'text/calendar' });
    const icsUrl = URL.createObjectURL(icsBlob);
    return { google, icsUrl };
  };

  const handleAddToAgenda = async () => {
    if (!isAuthenticated) { navigate(`/login?redirect=/player/event/${type}/${id}`); return; }
    setRegistering(true);
    try {
      if (isTournament) {
        await tournamentsApi.register(id);
      } else {
        await api.post(`/availabilities/${id}/join`);
      }
      toast.success("Ajouté à votre agenda Arcanapp !");
      // Show calendar export options
      const links = generateCalendarLinks(event);
      setCalendarLinks(links);
      setShowCalendarPopup(true);
      await loadEvent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setRegistering(false);
    }
  };

  const handleRemoveFromAgenda = async () => {
    setRegistering(true);
    try {
      if (isTournament) {
        await api.delete(`/tournaments/${id}/register`);
      } else if (isParticipant) {
        await api.delete(`/availabilities/${id}/leave`);
      } else {
        await api.delete(`/availabilities/${id}/interest`);
      }
      toast.success("Retiré de votre agenda");
      await loadEvent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setRegistering(false);
    }
  };

  const handleRegister = async () => {
    if (!isAuthenticated) { navigate(`/login?redirect=/player/event/${type}/${id}`); return; }
    setRegistering(true);
    try {
      if (isTournament) {
        await tournamentsApi.register(id);
      } else {
        await api.post(`/availabilities/${id}/join`);
      }
      toast.success(isTournament ? "Inscription réussie !" : "Vous avez rejoint la partie !");
      await loadEvent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setRegistering(false);
    }
  };

  const [interestLoading, setInterestLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const isInterested = !isTournament && event?.is_interested;
  const isCreator = event?.organizer_id === user?.id || event?.user_id === user?.id;

  const handleContactOrganizer = async () => {
    if (!isAuthenticated) { navigate(`/login?redirect=/player/event/${type}/${id}`); return; }
    setContactLoading(true);
    try {
      const organizerId = event.organizer_id || event.user_id;
      const convRes = await api.post("/conversations", { participants: [organizerId] });
      const convId = convRes.data?.id || convRes.data?.conversation_id;
      if (convId) {
        navigate(`/messaging?conversation=${convId}`);
      } else {
        navigate('/messaging');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création de la conversation");
    } finally {
      setContactLoading(false);
    }
  };
  
  const handleExpressInterest = async () => {
    if (!isAuthenticated) { navigate(`/login?redirect=/player/event/${type}/${id}`); return; }
    setInterestLoading(true);
    try {
      await api.post(`/availabilities/${id}/interest`);
      // Send auto message
      try {
        const gameName = getGameName(event.game);
        const dateLabel = new Date(event.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
        const convRes = await api.post("/conversations", { participants: [event.user_id] });
        const convId = convRes.data?.id || convRes.data?.conversation_id;
        if (convId) {
          await api.post(`/conversations/${convId}/messages`, { content: `Je souhaiterai participer à ta partie ${gameName} prévue le ${dateLabel}` });
        }
      } catch {}
      toast.success("L'organisateur a été prévenu !");
      await loadEvent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setInterestLoading(false);
    }
  };

  const handleDeclineInterest = async () => {
    setInterestLoading(true);
    try {
      await api.delete(`/availabilities/${id}/interest`);
      // Send decline message
      try {
        const gameName = getGameName(event.game);
        const dateLabel = new Date(event.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
        const convRes = await api.post("/conversations", { participants: [event.user_id] });
        const convId = convRes.data?.id || convRes.data?.conversation_id;
        if (convId) {
          await api.post(`/conversations/${convId}/messages`, { content: `Je ne peux plus participer à ta partie ${gameName} prévue le ${dateLabel}` });
        }
      } catch {}
      toast.success("L'organisateur a été prévenu.");
      await loadEvent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setInterestLoading(false);
    }
  };

  const handlePaidRegister = () => {
    if (!isAuthenticated) { navigate(`/login?redirect=/player/event/${type}/${id}`); return; }
    setShowPaymentModal(true);
  };

  const handleToggleFavShop = async () => {
    if (!isAuthenticated || !event?.shop_id) return;
    setFavShopLoading(true);
    const token = localStorage.getItem("access_token");
    try {
      if (isFavShop) {
        await axios.delete(`${API_URL}/api/users/favorite-shops/${event.shop_id}`, { headers: { Authorization: `Bearer ${token}` } });
        setIsFavShop(false);
        toast.success("Boutique retirée des favoris");
      } else {
        await axios.post(`${API_URL}/api/users/favorite-shops/${event.shop_id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setIsFavShop(true);
        toast.success("Boutique ajoutée aux favoris");
      }
    } catch { toast.error("Erreur"); }
    finally { setFavShopLoading(false); }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/e/${type}/${id}`;
    if (navigator.share) {
      navigator.share({ title: event?.name || "Événement Arcanapp", url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Lien copié !");
    }
  };

  const handleDuplicate = () => {
    if (!event) return;
    if (isTournament) {
      const params = new URLSearchParams({
        duplicate: 'true',
        name: event.name || '',
        game: event.game || '',
        format: event.format || '',
        description: event.description || '',
        price: String(event.price || 0),
        capacity: String(event.capacity || 32),
        venue_name: event.venue?.name || '',
        venue_address: event.venue?.address || '',
        venue_city: event.venue?.city || '',
        is_team: String(event.is_team || false),
        team_size: String(event.team_size || 2),
      });
      navigate(`/create-tournament?${params.toString()}`);
    } else {
      const params = new URLSearchParams({
        duplicate: 'true',
        game: event.game || '',
        description: event.description || '',
        max_players: String(event.max_players || 4),
        location_type: event.location?.type || 'store',
        location_name: event.location?.name || '',
        location_city: event.location?.city || '',
      });
      navigate(`/create-tournament?${params.toString()}`);
    }
  };

  const deepLink = `arcanapp://${type}/${id}`;
  const isRegistered = isTournament && event?.participants?.includes(user?.id);
  const isParticipant = !isTournament && (event?.is_participant || event?.participants?.some(p => p.user_id === user?.id || p === user?.id));

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>;
  if (!event) return <div className="text-center py-16 text-[#666]">Événement non trouvé</div>;

  const gc = getGameColor(event.game);
  const isFull = isTournament && event.capacity > 0 && (event.participants_count || event.participants?.length || 0) >= event.capacity;
  const isAutoWaitlist = event.waitlist_mode === 'automatic';
  // Organizer display (real shop name + logo + auto banner)
  const orgName = event.organizer?.company_name || orgProfile?.shop_name || event.organizer?.name || orgProfile?.name || event.source_name || "Organisateur";
  const orgLogo = absUrl(event.organizer?.logo_url || event.organizer?.avatar_url || orgProfile?.logo_url || orgProfile?.avatar_url);
  const bannerAbs = absUrl(event.banner_url);
  // FOMO: remaining seats <= 20% of capacity
  const capacityNum = event.capacity || event.max_participants || 0;
  const participantsNum = event.participants_count || event.participants?.length || 0;
  const remainingSeats = capacityNum > 0 ? Math.max(0, capacityNum - participantsNum) : null;
  const showFomo = isTournament && remainingSeats != null && remainingSeats > 0 && remainingSeats <= Math.ceil(capacityNum * 0.2);
  // Live countdown until the event start (e.g. "J-3 · 14h")
  const countdownLabel = (() => {
    if (!isTournament || !event.date) return null;
    const diff = new Date(event.date).getTime() - nowTs;
    if (diff <= 0) return null;
    const totalMin = Math.floor(diff / 60000);
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;
    if (days >= 1) return `J-${days} · ${hours}h`;
    if (hours >= 1) return `${hours}h ${String(mins).padStart(2, "0")}min`;
    return `${mins} min`;
  })();
  const fmtRemaining = (ms) => {
    if (ms == null) return '';
    const s = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  };

  // Partage réseaux sociaux (aperçu riche via endpoint OpenGraph)
  const shareOgUrl = `${API_URL}/api/share/${type}/${id}`;
  const shareText = `${event.name || getGameName(event.game)}${event.format ? ` (${event.format})` : ''}${event.date ? ` le ${formatDate(event.date)}` : ''}`.trim();
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareOgUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareOgUrl)}`,
  };

  return (
    <div data-testid="player-event-page">
      {/* Back + Share */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[#B0B0B0] hover:text-white text-sm shrink-0" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <button onClick={handleDuplicate} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-xs sm:text-sm" data-testid="duplicate-event-btn">
            <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">Dupliquer</span>
          </button>
          <button onClick={handleShare} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-xs sm:text-sm" data-testid="share-btn">
            <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">Partager</span>
          </button>
          {/* Menu partage réseaux sociaux */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg bg-[#1877F2]/10 hover:bg-[#1877F2]/20 text-[#1877F2] text-xs sm:text-sm" data-testid="share-social-btn" title="Partager sur les réseaux">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#1877F2]"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <span className="hidden sm:inline">Réseaux</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-[#333] w-48">
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2" data-testid="share-facebook">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#1877F2]"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  <span className="text-white text-sm">Facebook</span>
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#252525]">
                <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2" data-testid="share-whatsapp">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#25D366]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  <span className="text-white text-sm">WhatsApp</span>
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(shareOgUrl + '\n\n' + shareText); toast.success('Lien copié pour Instagram !'); }} className="cursor-pointer focus:bg-[#252525] flex items-center gap-2" data-testid="share-instagram">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#E4405F]"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/></svg>
                <span className="text-white text-sm">Instagram</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(shareText + '\n' + shareOgUrl); toast.success('Lien copié pour Discord !'); }} className="cursor-pointer focus:bg-[#252525] flex items-center gap-2" data-testid="share-discord">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#5865F2]"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/></svg>
                <span className="text-white text-sm">Discord</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Card className="bg-[#1a1a1a] border-[#333] overflow-hidden">
        {isTournament && bannerAbs ? (
          <img src={bannerAbs} alt={event.name || "Bannière"} className="w-full h-auto block bg-[#0f0f0f]" data-testid="event-banner" />
        ) : (
          <div className="h-1.5 w-full" style={{ backgroundColor: isTournament ? gc : "#4ECDC4" }} />
        )}
        <CardContent className="p-6 space-y-5">
          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge style={{ backgroundColor: `${gc}20`, color: gc }} className="text-sm font-semibold gap-1">
              {isTournament ? <Trophy className="w-3.5 h-3.5" /> : <Gamepad2 className="w-3.5 h-3.5" />}
              {getGameName(event.game)}
            </Badge>
            {event.format && <Badge className="bg-[#252525] text-[#B0B0B0]">{event.format}</Badge>}
            <Badge className={isTournament ? "bg-[#FF6B35]/10 text-[#FF6B35]" : "bg-[#4ECDC4]/10 text-[#4ECDC4]"}>
              {isTournament ? "Tournoi" : "Partie"}
            </Badge>
            {event.status === "in_progress" && <Badge className="bg-green-500/20 text-green-400">En cours</Badge>}
            {isTournament && event.is_team && (
              <Badge className="bg-purple-500/15 text-purple-400">
                <Users className="w-3 h-3 mr-1" />Équipe de {event.team_size || 2}
              </Badge>
            )}
          </div>

          {/* Title + Edit button for creator */}
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-white flex-1">{event.name || event.description || `Partie ${getGameName(event.game)}`}</h1>
            {isAuthenticated && isCreator && !isTournament && (
              <button onClick={startEditing} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#252525] hover:bg-[#333] text-[#B0B0B0] hover:text-white text-sm transition-colors" data-testid="edit-party-btn">
                <Pencil className="w-3.5 h-3.5" /> Modifier
              </button>
            )}
          </div>
          {event.description && (
            <div
              className="text-[#B0B0B0] text-sm sm:text-base whitespace-pre-line break-words event-description [&_a]:text-[#FF6B35] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              data-testid="event-description"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }}
            />
          )}

          {/* Info grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            <div className="flex items-center gap-2 text-[#B0B0B0]"><Calendar className="w-4 h-4 text-[#FF6B35] flex-shrink-0" /><span className="text-xs sm:text-sm">{formatDate(event.date)}</span></div>
            {(event.venue?.city || event.location?.city) && (
              <div className="flex items-center gap-2 text-[#B0B0B0]"><MapPin className="w-4 h-4 text-[#4ECDC4]" /><span className="text-sm">{event.venue?.name || event.location?.name || event.venue?.city || event.location?.city}</span></div>
            )}
            <div className="flex items-center gap-2 text-[#B0B0B0]"><Users className="w-4 h-4 text-[#7B68EE]" /><span className="text-sm">{event.participants_count || event.participants?.length || 0}/{event.capacity || event.max_participants || event.max_players || "?"}</span></div>
            {isTournament && <div className="flex items-center gap-2 text-[#B0B0B0]"><Euro className="w-4 h-4 text-[#FFCB05]" /><span className="text-sm">{!event.price && event.price !== 0 ? "—" : event.price === 0 ? "Gratuit" : `${event.price}€`}</span></div>}
          </div>

          {/* Add to calendar line */}
          {event.date && (
            <div className="flex items-center gap-3 flex-wrap pt-1" data-testid="event-add-calendar-line">
              <span className="text-[#888] text-sm flex items-center gap-1.5"><CalendarPlus className="w-4 h-4 text-[#4ECDC4]" />Ajouter à mon agenda :</span>
              <AddToCalendar
                title={event.name || getGameName(event.game)}
                start={event.date}
                description={`${event.format ? event.format + ' · ' : ''}${(event.description || '').replace(/<[^>]+>/g, '').slice(0, 300)}`}
                location={event.venue?.name || event.venue?.address || event.venue?.city || event.location?.city || ''}
              />
            </div>
          )}

          {/* FOMO message when few seats remain (<= 20%) */}
          {showFomo && (
            <div className="flex items-start gap-2.5 rounded-lg bg-[#FF6B35]/10 border border-[#FF6B35]/30 px-4 py-3" data-testid="fomo-message">
              <Bell className="w-5 h-5 text-[#FF6B35] shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1.5">
                <p className="text-[#FF6B35] text-sm font-medium">
                  Il ne reste plus que {remainingSeats} place{remainingSeats > 1 ? "s" : ""} pour cet évènement, ne passez pas à côté : inscrivez vous dès maintenant&nbsp;!
                </p>
                {countdownLabel && (
                  <div className="inline-flex items-center gap-1.5 rounded-md bg-[#FF6B35]/15 px-2.5 py-1 text-[#FF6B35] text-xs font-semibold" data-testid="fomo-countdown">
                    <Clock className="w-3.5 h-3.5" /> Commence dans {countdownLabel}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Organizer / Shop */}
          {isShopTournament ? (
            <div className="pt-3 border-t border-[#333] space-y-3" data-testid="shop-organizer-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {orgLogo ? (
                    <img src={orgLogo} alt={event.source_name || orgName} className="w-10 h-10 rounded-full object-cover border border-[#333] bg-[#252525]" data-testid="organizer-logo" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#252525] flex items-center justify-center text-[#FF6B35] font-bold text-sm">
                      <Store className="w-5 h-5" />
                    </div>
                  )}
                  <div>
                    <p className="text-white text-sm font-medium" data-testid="organizer-name">{event.source_name || event.organizer?.company_name || event.organizer?.name}</p>
                    <p className="text-[#666] text-xs">Boutique</p>
                  </div>
                </div>
                {isAuthenticated && event.shop_id && (
                  <button onClick={handleToggleFavShop} disabled={favShopLoading}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                      isFavShop ? "bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/30" : "bg-[#252525] text-[#B0B0B0] hover:text-white hover:bg-[#333]"
                    }`} data-testid="toggle-fav-shop-btn">
                    <Heart className={`w-3.5 h-3.5 ${isFavShop ? "fill-[#FF6B35]" : ""}`} />
                    {favShopLoading ? "..." : isFavShop ? "Boutique favorite" : "Ajouter aux favoris"}
                  </button>
                )}
              </div>
            </div>
          ) : (event.organizer || orgProfile) && (
            <div className="flex items-center gap-3 pt-3 border-t border-[#333]" data-testid="organizer-section">
              {orgLogo ? (
                <img src={orgLogo} alt={orgName} className="w-10 h-10 rounded-full object-cover border border-[#333] bg-[#252525]" data-testid="organizer-logo" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#252525] flex items-center justify-center text-[#FF6B35] font-bold text-sm">
                  {orgName?.charAt(0)?.toUpperCase() || "?"}
                </div>
              )}
              <div>
                <p className="text-white text-sm font-medium" data-testid="organizer-name">{orgName}</p>
                <p className="text-[#666] text-xs">{orgProfile?.role === "ORG" ? "Boutique organisatrice" : "Organisateur"}</p>
              </div>
            </div>
          )}

          {/* Shop stats */}
          {!isShopTournament && orgProfile?.shop_stats && (
            <div className="flex gap-3 pt-2" data-testid="shop-stats">
              <div className="flex-1 rounded-lg bg-[#252525] p-3 text-center">
                <p className="text-[#FF6B35] text-lg font-bold">{orgProfile.shop_stats.tournaments_per_week}</p>
                <p className="text-[#888] text-xs">tournois / sem.</p>
              </div>
              <div className="flex-1 rounded-lg bg-[#252525] p-3 text-center">
                <p className="text-[#4ECDC4] text-lg font-bold">{orgProfile.shop_stats.parties_per_week}</p>
                <p className="text-[#888] text-xs">parties / sem.</p>
              </div>
            </div>
          )}

          {/* Shop play conditions */}
          {!isShopTournament && orgProfile?.role === "ORG" && (
            <div className="pt-2">
              <p className="text-[#888] text-xs font-medium mb-1">Conditions de jeu libre et tarif</p>
              {orgProfile.shop_play_conditions ? (
                <p className="text-[#B0B0B0] text-sm bg-[#252525] rounded-lg p-3">{orgProfile.shop_play_conditions}</p>
              ) : (
                <p className="text-[#555] text-xs italic">{orgProfile.shop_name || orgProfile.name} n'a pas renseigne cette information.</p>
              )}
            </div>
          )}

          {/* Register / Interest / Agenda */}
          <div className="pt-3 border-t border-[#333]">
            {isTournament && isAutoWaitlist && event.online_registration !== false && !isRegistered && (isFull || waitlistStatus?.in_waitlist || waitlistStatus?.offer) ? (
              waitlistStatus?.offer ? (
                <div className="space-y-3" data-testid="waitlist-offer-block">
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400">
                    <Bell className="w-5 h-5 shrink-0" />
                    <div className="text-sm">
                      <p className="font-bold">Une place s'est libérée !</p>
                      <p className="text-green-300/80">Il vous reste <strong>{fmtRemaining(offerRemaining)}</strong> pour confirmer votre place.</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button onClick={handleConfirmWaitlist} disabled={waitlistLoading || offerRemaining === 0}
                      className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors text-sm sm:text-base disabled:opacity-50" data-testid="waitlist-confirm-btn">
                      <Check className="w-4 h-4" /> {waitlistLoading ? "..." : "Confirmer ma place"}
                    </button>
                    <button onClick={handleDeclineWaitlist} disabled={waitlistLoading}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/20 transition-colors text-sm disabled:opacity-50" data-testid="waitlist-decline-btn">
                      <ThumbsDown className="w-4 h-4" /> Refuser
                    </button>
                  </div>
                </div>
              ) : waitlistStatus?.in_waitlist ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium" data-testid="waitlist-position">
                    <ListOrdered className="w-4 h-4" /> En file d'attente — position {waitlistStatus.position}
                  </div>
                  <button onClick={handleLeaveWaitlist} disabled={waitlistLoading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/20 transition-colors text-sm disabled:opacity-50" data-testid="waitlist-leave-btn">
                    <CalendarMinus className="w-4 h-4" /> Quitter la file
                  </button>
                </div>
              ) : !isAuthenticated ? (
                <Link to={`/login?redirect=/player/event/${type}/${id}`}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#E55A25] transition-colors text-sm sm:text-base" data-testid="login-to-register">
                  Se connecter pour rejoindre la file
                </Link>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[#888] text-sm"><Users className="w-4 h-4" /> Tournoi complet ({waitlistStatus?.waitlist_count || 0} en attente)</div>
                  <button onClick={handleJoinWaitlist} disabled={waitlistLoading}
                    className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#4ECDC4] hover:bg-[#3DB8B0] text-black font-medium transition-colors text-sm sm:text-base disabled:opacity-50" data-testid="waitlist-join-btn">
                    <ListOrdered className="w-4 h-4" /> {waitlistLoading ? "..." : "Rejoindre la file d'attente"}
                  </button>
                </div>
              )
            ) : !isAuthenticated ? (
              <Link to={`/login?redirect=/player/event/${type}/${id}`}
                className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#E55A25] transition-colors text-sm sm:text-base" data-testid="login-to-register">
                Se connecter
              </Link>
            ) : !isTournament && isCreator ? (
              <div className="flex items-center gap-2 text-[#B0B0B0]">
                <Users className="w-5 h-5" />
                <span className="font-medium">Vous organisez cette partie</span>
              </div>
            ) : !isTournament && isParticipant ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-500/10 text-green-400 text-sm font-medium">
                  <Check className="w-4 h-4" />Vous participez (confirmé)
                </div>
                <button onClick={handleRemoveFromAgenda} disabled={registering}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/20 transition-colors text-sm disabled:opacity-50" data-testid="remove-from-agenda-btn">
                  <CalendarMinus className="w-4 h-4" /> {registering ? "..." : "Retirer de mon agenda"}
                </button>
                <button onClick={() => { const links = generateCalendarLinks(event); setCalendarLinks(links); setShowCalendarPopup(true); }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#252525] border border-[#333] text-[#B0B0B0] hover:text-white hover:bg-[#333] transition-colors text-sm" data-testid="export-calendar-btn">
                  <Calendar className="w-4 h-4" /> Exporter
                </button>
              </div>
            ) : !isTournament && isInterested ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium">
                  <Bookmark className="w-4 h-4" />En attente de confirmation
                </div>
                <button onClick={handleDeclineInterest} disabled={interestLoading}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/20 transition-colors text-sm disabled:opacity-50" data-testid="decline-btn">
                  <ThumbsDown className="w-4 h-4" /> {interestLoading ? "..." : "Ça ne m'intéresse plus"}
                </button>
              </div>
            ) : !isTournament ? (
              <button onClick={handleExpressInterest} disabled={interestLoading}
                className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#4ECDC4] hover:bg-[#3DB8B0] text-black font-medium transition-colors text-sm sm:text-base disabled:opacity-50" data-testid="interest-btn">
                {interestLoading ? "..." : "Ça m'intéresse"}
              </button>
            ) : isTournament && event.is_team ? (
              isRegistered ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-500/10 text-green-400 text-sm font-medium">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Vous êtes inscrit (équipe)</span>
                  </div>
                  <button onClick={handleRemoveFromAgenda} disabled={registering}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/20 transition-colors text-sm disabled:opacity-50" data-testid="remove-from-agenda-btn">
                    <CalendarMinus className="w-4 h-4" /> {registering ? "..." : "Retirer de mon agenda"}
                  </button>
                  <button onClick={() => { const links = generateCalendarLinks(event); setCalendarLinks(links); setShowCalendarPopup(true); }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#252525] border border-[#333] text-[#B0B0B0] hover:text-white hover:bg-[#333] transition-colors text-sm" data-testid="export-calendar-btn">
                    <Calendar className="w-4 h-4" /> Exporter
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <button onClick={() => setShowTeamModal(true)}
                    className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors text-sm sm:text-base"
                    data-testid="register-team-btn">
                    <Users className="w-4 h-4" /> Inscrire une équipe de {event.team_size || 2}
                  </button>
                </div>
              )
            ) : isTournament && event.price > 0 ? (
              isRegistered ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-500/10 text-green-400 text-sm font-medium">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Vous êtes inscrit</span>
                  </div>
                  <button onClick={handleRemoveFromAgenda} disabled={registering}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/20 transition-colors text-sm disabled:opacity-50" data-testid="remove-from-agenda-btn">
                    <CalendarMinus className="w-4 h-4" /> {registering ? "..." : "Retirer de mon agenda"}
                  </button>
                  <button onClick={() => { const links = generateCalendarLinks(event); setCalendarLinks(links); setShowCalendarPopup(true); }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#252525] border border-[#333] text-[#B0B0B0] hover:text-white hover:bg-[#333] transition-colors text-sm" data-testid="export-calendar-btn">
                    <Calendar className="w-4 h-4" /> Exporter
                  </button>
                </div>
              ) : event.source === "scraper" && event.source_url ? (
                <a href={event.source_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors text-sm sm:text-base"
                  data-testid="website-organizer-btn">
                  <Globe className="w-4 h-4" /> Voir l'événement sur le site
                </a>
              ) : (event.online_registration !== false && !isFull) ? (
                <button onClick={handlePaidRegister} disabled={registering}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors disabled:opacity-50 text-sm sm:text-base"
                  data-testid="register-tournament-btn">
                  <Euro className="w-4 h-4" /> S'inscrire ({event.price}€)
                </button>
              ) : (
                <button onClick={handleContactOrganizer} disabled={contactLoading}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors disabled:opacity-50 text-sm sm:text-base"
                  data-testid="contact-organizer-btn">
                  <MessageSquare className="w-4 h-4" /> {contactLoading ? "..." : "Contacter l'organisateur"}
                </button>
              )
            ) : isRegistered || isParticipant ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-500/10 text-green-400 text-sm font-medium">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">{isTournament ? "Vous êtes inscrit" : "Vous participez"}</span>
                </div>
                <button onClick={handleRemoveFromAgenda} disabled={registering}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-medium hover:bg-red-500/20 transition-colors text-sm disabled:opacity-50" data-testid="remove-from-agenda-btn">
                  <CalendarMinus className="w-4 h-4" /> {registering ? "..." : "Retirer de mon agenda"}
                </button>
                <button onClick={() => { const links = generateCalendarLinks(event); setCalendarLinks(links); setShowCalendarPopup(true); }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#252525] border border-[#333] text-[#B0B0B0] hover:text-white hover:bg-[#333] transition-colors text-sm" data-testid="export-calendar-btn">
                  <Calendar className="w-4 h-4" /> Exporter
                </button>
              </div>
            ) : isTournament ? (
              event.source === "scraper" && event.source_url ? (
                <a href={event.source_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors text-sm sm:text-base"
                  data-testid="website-organizer-btn">
                  <Globe className="w-4 h-4" /> Voir l'événement sur le site
                </a>
              ) : (event.online_registration !== false && !isFull) ? (
                <button onClick={handleAddToAgenda} disabled={registering}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors disabled:opacity-50 text-sm sm:text-base"
                  data-testid="register-tournament-btn">
                  <Check className="w-4 h-4" /> {registering ? "..." : "S'inscrire au tournoi"}
                </button>
              ) : (
                <button onClick={handleContactOrganizer} disabled={contactLoading}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors disabled:opacity-50 text-sm sm:text-base"
                  data-testid="contact-organizer-btn">
                  <MessageSquare className="w-4 h-4" /> {contactLoading ? "..." : "Contacter l'organisateur"}
                </button>
              )
            ) : (
              <button onClick={handleAddToAgenda} disabled={registering}
                className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#4ECDC4] hover:bg-[#3DB8B0] text-black font-medium transition-colors disabled:opacity-50 text-sm sm:text-base"
                data-testid="add-to-agenda-btn">
                <CalendarPlus className="w-4 h-4" /> {registering ? "..." : "Ajouter à mon agenda"}
              </button>
            )}
          </div>

          {/* Edit form for party creator */}
          {editing && !isTournament && (
            <div className="pt-3 border-t border-[#333] space-y-3" data-testid="edit-party-form">
              <h3 className="text-sm font-bold text-[#666] uppercase flex items-center gap-2"><Pencil className="w-3.5 h-3.5" /> Modifier la partie</h3>
              <div>
                <label className="text-xs text-[#888] mb-1 block">Description</label>
                <textarea value={editData.description || ""} onChange={e => setEditData(d => ({...d, description: e.target.value}))}
                  className="w-full bg-[#252525] border border-[#444] rounded-lg px-3 py-2 text-white text-sm focus:border-[#4ECDC4] outline-none resize-none" rows={3} data-testid="edit-description" />
              </div>
              <div>
                <label className="text-xs text-[#888] mb-1 block">Nombre de joueurs max</label>
                <input type="number" min={2} max={20} value={editData.max_players || 4} onChange={e => setEditData(d => ({...d, max_players: parseInt(e.target.value) || 4}))}
                  className="w-32 bg-[#252525] border border-[#444] rounded-lg px-3 py-2 text-white text-sm focus:border-[#4ECDC4] outline-none" data-testid="edit-max-players" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveEdit} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4ECDC4] hover:bg-[#3DB8B0] text-black text-sm font-medium transition-colors" data-testid="save-edit-btn">
                  <Save className="w-3.5 h-3.5" /> Enregistrer
                </button>
                <button onClick={() => setEditing(false)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#252525] hover:bg-[#333] text-[#B0B0B0] text-sm transition-colors" data-testid="cancel-edit-btn">
                  <X className="w-3.5 h-3.5" /> Annuler
                </button>
              </div>
            </div>
          )}

          {/* Interested users management for party creator */}
          {isAuthenticated && !isTournament && isCreator && (
            <div className="pt-3 border-t border-[#333]" data-testid="interested-users-section">
              <h3 className="text-sm font-bold text-[#666] uppercase mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#4ECDC4]" /> Joueurs intéressés ({interestedUsers.length})
              </h3>
              {loadingInterested ? (
                <p className="text-[#666] text-sm">Chargement...</p>
              ) : interestedUsers.length === 0 ? (
                <p className="text-[#555] text-sm italic">Aucun joueur en attente de validation</p>
              ) : (
                <div className="space-y-2">
                  {interestedUsers.map(u => (
                    <div key={u.id} className="flex items-center justify-between bg-[#252525] rounded-lg px-3 py-2.5" data-testid={`interested-user-${u.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#333] flex items-center justify-center text-[#FF6B35] font-bold text-xs">
                          {(u.pseudo || u.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{u.pseudo || u.name}</p>
                          {u.city && <p className="text-[#666] text-xs">{u.city}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleConfirmUser(u.id)} disabled={confirmingUser === u.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500/15 text-green-400 hover:bg-green-500/25 text-xs font-medium transition-colors disabled:opacity-50" data-testid={`accept-user-${u.id}`}>
                          <UserCheck className="w-3.5 h-3.5" /> {confirmingUser === u.id ? "..." : "Accepter"}
                        </button>
                        <button onClick={() => handleRejectUser(u.id)} disabled={rejectingUser === u.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs font-medium transition-colors disabled:opacity-50" data-testid={`reject-user-${u.id}`}>
                          <UserX className="w-3.5 h-3.5" /> {rejectingUser === u.id ? "..." : "Refuser"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Teammate Search - only for team events or multi-player parties */}
      {event && (event.is_team || (!isTournament && (event.max_players > 2 || event.capacity > 2))) && (
        <Card className="bg-[#1a1a1a] border-[#333] mt-4">
          <CardContent className="p-4">
            <TeammateSearch
              eventType={isTournament ? 'tournament' : 'availability'}
              eventId={event.id}
              eventName={event.name || event.description}
            />
          </CardContent>
        </Card>
      )}

      {/* Live standings (if tournament in progress) */}
      {isTournament && standings.length > 0 && (
        <Card className="bg-[#1a1a1a] border-[#333] mt-4">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-[#666] uppercase mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4 text-[#FF6B35]" /> Classement en direct
            </h3>
            <div className="space-y-1">
              {standings.slice(0, 8).map((s, i) => (
                <div key={s.player_id} className={`flex items-center justify-between py-1.5 px-2 rounded ${i < 3 ? "bg-[#252525]" : ""} ${s.dropped ? "opacity-40" : ""}`} data-testid={`live-standing-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold w-5 ${i === 0 ? "text-yellow-400" : i === 1 ? "text-gray-300" : i === 2 ? "text-orange-400" : "text-[#666]"}`}>#{s.rank}</span>
                    <span className={`text-sm ${s.dropped ? "line-through text-[#666]" : "text-white"}`}>{s.player_name}</span>
                  </div>
                  <span className="text-sm font-bold text-[#FF6B35]">{s.match_points} pts</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current round pairings */}
      {currentRound && (
        <Card className="bg-[#1a1a1a] border-[#333] mt-4">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-[#666] uppercase mb-3 flex items-center gap-2">
              <Swords className="w-4 h-4 text-[#4ECDC4]" /> Ronde {currentRound.round_number}
              <span className={`text-xs px-2 py-0.5 rounded ${currentRound.status === "completed" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                {currentRound.status === "completed" ? "Terminée" : "En cours"}
              </span>
            </h3>
            <div className="space-y-1">
              {currentRound.pairings.map((p, i) => {
                const isMe = p.player1_id === user?.id || p.player2_id === user?.id;
                return (
                  <div key={p.id} className={`flex items-center gap-3 py-2 px-2 rounded text-sm ${isMe ? "bg-[#FF6B35]/10 border border-[#FF6B35]/30" : ""}`} data-testid={`pairing-${i}`}>
                    <span className="text-[#666] text-xs font-mono w-8">T{p.table}</span>
                    <span className={`flex-1 ${p.winner_id === p.player1_id ? "text-green-400 font-medium" : "text-white"}`}>{p.player1_name}</span>
                    {p.result ? (
                      <span className="text-[#FF6B35] text-xs font-bold">{p.result.p1_wins}-{p.result.p2_wins}</span>
                    ) : (
                      <span className="text-[#666] text-xs">vs</span>
                    )}
                    <span className={`flex-1 text-right ${p.winner_id === p.player2_id ? "text-green-400 font-medium" : p.player2_id ? "text-white" : "text-[#666] italic"}`}>{p.player2_name}</span>
                  </div>
                );
              })}
            </div>
            {/* Link to life counter if user has a match */}
            {user && currentRound.pairings.some(p => (p.player1_id === user.id || p.player2_id === user.id) && !p.result) && (
              <div className="mt-3 pt-3 border-t border-[#333]">
                <Link to={`/player/life-counter?startLife=20`}
                  className="flex items-center gap-2 text-sm text-[#4ECDC4] hover:text-[#3DB8B0]" data-testid="life-counter-link">
                  <Swords className="w-4 h-4" /> Ouvrir le compteur de vie (20 PV)
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Teams section for team tournaments */}
      {isTournament && event.is_team && (
        <div className="mt-4">
          <TeamsList tournament={event} user={user} onRefresh={loadEvent} />
        </div>
      )}

      {/* Calendar Export Popup */}
      {showCalendarPopup && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowCalendarPopup(false)} data-testid="calendar-popup">
          <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg">Ajouter au calendrier</h3>
              <button onClick={() => setShowCalendarPopup(false)} className="text-[#666] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[#888] text-sm mb-4">L'événement est dans votre agenda Arcanapp. Ajoutez-le aussi à votre calendrier externe :</p>
            <div className="space-y-2">
              <a href={calendarLinks.google} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-[#252525] hover:bg-[#333] text-white transition-colors text-sm font-medium" data-testid="add-google-calendar">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24"><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" fill="#4285F4"/><path d="M12 7v5l4.25 2.52" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>
                Google Agenda
              </a>
              <a href={calendarLinks.icsUrl} download="arcanapp-event.ics"
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-[#252525] hover:bg-[#333] text-white transition-colors text-sm font-medium" data-testid="add-ics-calendar">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="#FF6B35" strokeWidth="1.5"/><line x1="16" y1="2" x2="16" y2="6" stroke="#FF6B35" strokeWidth="1.5"/><line x1="8" y1="2" x2="8" y2="6" stroke="#FF6B35" strokeWidth="1.5"/><line x1="3" y1="10" x2="21" y2="10" stroke="#FF6B35" strokeWidth="1.5"/></svg>
                Apple / Outlook (fichier .ics)
              </a>
            </div>
            <button onClick={() => setShowCalendarPopup(false)} className="w-full mt-4 py-2 text-[#666] text-sm hover:text-white transition-colors">
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Stripe Payment Modal */}
      {showPaymentModal && user && event && (
        <StripePaymentModal
          tournament={event}
          user={user}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={loadEvent}
        />
      )}

      {/* Team registration modal */}
      {showTeamModal && user && event && (
        <TeamRegistrationModal
          tournament={event}
          user={user}
          onClose={() => setShowTeamModal(false)}
          onSuccess={loadEvent}
        />
      )}
    </div>
  );
}
